
import { inject } from '@angular/core';
import { injectTranslation, RootContext, RootContextBase } from '@skylab-angular-shared/core';
import { CompanyParametersQuery, withFeature, withModule, withPermission } from '@skylab-shared/business';
import { InventoryDocumentKind, PurchaseDocumentKind, SaleDocumentKind, UserPermissionKind } from '@skylab-shared/dataModel';
import { AppStates, NavigationRoute } from '@skylab-shared/scenes';
import { IconName } from '@skylab-assets/icons';

const ID_PREFIX = 'navigation-' as const;

export interface MenuItem {
	id: `${typeof ID_PREFIX}${string}`;
	label: string;
	icon?: IconName;
	hrefObj: NavigationRoute;
	addHrefObj?: NavigationRoute;
	list?: MenuItem[];
	closed?: boolean;
	notifier?: boolean;
	isActive?: (params: any) => boolean;
	isBottom?: boolean;
}

export function getMenuItems() {
	const _T = injectTranslation();

	return {
		dashboard: {
			label: _T('navigation.item.dashboard'),
			icon: 'desktop-regular',
			hrefObj: { url: AppStates.dashboard.url },
			id: `${ID_PREFIX}dashboard`,
			notifier: true
		},
		documentSale: {
			label: _T('navigation.item.document-sale'),
			icon: 'sale',
			hrefObj: { url: AppStates.documentSale.url },
			isActive: (params: { viewKind: string }) => !params.viewKind,
			list: [] as MenuItem[],
			id: `${ID_PREFIX}document-sale`,
		},
		documentSaleInvoice: {
			label: _T('navigation.item.invoice'),
			hrefObj: { url: AppStates.documentSale.url, params: { viewKind: SaleDocumentKind.Invoice } },
			addHrefObj: { url: AppStates.newDocumentSale.url, params: { kind: SaleDocumentKind.Invoice } },
			isActive: (params: { viewKind: string }) => params.viewKind == SaleDocumentKind.Invoice,
			id: `${ID_PREFIX}invoice`,
		},
		documentSaleReceipt: {
			label: _T('navigation.item.receipt'),
			hrefObj: { url: AppStates.documentSale.url, params: { viewKind: SaleDocumentKind.Receipt } },
			addHrefObj: { url: AppStates.newDocumentSale.url, params: { kind: SaleDocumentKind.Receipt } },
			isActive: (params: { viewKind: string }) => params.viewKind == SaleDocumentKind.Receipt,
			id: `${ID_PREFIX}receipt`,
		},
		documentSaleInvoiceMargin: {
			label: _T('navigation.item.vat-margin-invoice'),
			hrefObj: { url: AppStates.documentSale.url, params: { viewKind: SaleDocumentKind.VatMarginInvoice } },
			addHrefObj: { url: AppStates.newDocumentSale.url, params: { kind: SaleDocumentKind.VatMarginInvoice } },
			isActive: (params: { viewKind: string }) => params.viewKind == SaleDocumentKind.VatMarginInvoice,
			id: `${ID_PREFIX}vat-margin-invoice`,
		},
		documentSaleProForma: {
			label: _T('navigation.item.pro-forma'),
			hrefObj: { url: AppStates.documentSale.url, params: { viewKind: SaleDocumentKind.ProForma } },
			addHrefObj: { url: AppStates.newProFormaDocument.url },
			isActive: (params: { viewKind: string }) => params.viewKind == SaleDocumentKind.ProForma,
			id: `${ID_PREFIX}pro-forma`,
		},
		documentSaleAdvanceInvoice: {
			label: _T('navigation.item.advance-invoice'),
			hrefObj: { url: AppStates.documentSale.url, params: { viewKind: SaleDocumentKind.AdvanceInvoice } },
			addHrefObj: { url: AppStates.newAdvanceInvoiceDocument.url },
			isActive: (params: { viewKind: string }) => params.viewKind == SaleDocumentKind.AdvanceInvoice,
			id: `${ID_PREFIX}advance-invoice`,
		},
		documentSaleSalePattern: {
			label: _T('navigation.item.sale-pattern'),
			hrefObj: { url: AppStates.salePattern.url },
			addHrefObj: { url: AppStates.newSalePattern.url },
			isActive: (params: { viewKind: string }) => !params.viewKind,
			id: `${ID_PREFIX}sale-pattern`,
		},
		purchaseDocument: {
			label: _T('navigation.item.purchase-document'),
			icon: 'wallet-credit-card-regular',
			hrefObj: { url: AppStates.purchases.url },
			list: [] as MenuItem[],
			isActive: (params: { viewKind: string }) => !params.viewKind,
			id: `${ID_PREFIX}purchase-document`,
		},
		purchaseDocumentExpanse: {
			label: _T('navigation.item.expenses'),
			hrefObj: { url: AppStates.purchases.url, params: { viewKind: PurchaseDocumentKind.Expense } },
			addHrefObj: { url: AppStates.newPurchaseExpense.url, params: { kind: PurchaseDocumentKind.Expense } },
			isActive: (params: { viewKind: string }) => params.viewKind === PurchaseDocumentKind.Expense,
			id: `${ID_PREFIX}expenses`,
		},
		purchaseDocumentInvoice: {
			label: _T('navigation.item.purchase-invoice'),
			hrefObj: { url: AppStates.purchases.url, params: { viewKind: PurchaseDocumentKind.PurchaseInvoice } },
			addHrefObj: { url: AppStates.newPurchaseInvoice.url, params: { kind: PurchaseDocumentKind.PurchaseInvoice } },
			isActive: (params: { viewKind: string }) => params.viewKind === PurchaseDocumentKind.PurchaseInvoice,
			id: `${ID_PREFIX}purchase-invoice`,
		},
		orders: {
			label: _T('navigation.item.orders'),
			icon: 'cart',
			hrefObj: { url: AppStates.orders.url },
			addHrefObj: { url: AppStates.newOrder.url },
			id: `${ID_PREFIX}orders`,
		},
		products: {
			label: _T('navigation.item.products'),
			icon: 'box-regular',
			hrefObj: { url: AppStates.products.url },
			addHrefObj: { url: AppStates.newProduct.url },
			id: `${ID_PREFIX}products`,
		},
		clients: {
			label: _T('navigation.item.clients'),
			icon: 'people-regular',
			hrefObj: { url: AppStates.clients.url },
			addHrefObj: { url: AppStates.newClient.url },
			id: `${ID_PREFIX}clients`,
		},
		accountancy: {
			label: _T('navigation.item.accountancy'),
			icon: 'money-calculator-regular',
			hrefObj: { url: AppStates.accountancy.url },
			addHrefObj: { url: AppStates.newAccountancy.url },
			id: `${ID_PREFIX}accountancy`,
		},
		reports: {
			label: _T('navigation.item.reports'),
			icon: 'chart-multiple-regular',
			hrefObj: { url: AppStates.reports.url },
			id: `${ID_PREFIX}reports`,
		},
		reportBalanceSheet: {
			label: _T('scenes.reports.balance-sheet.name'),
			hrefObj: { url: AppStates.reportsBalanceSheet.url },
			id: `${ID_PREFIX}report-balance-sheet`,
		},
		reportSalesByClients: {
			label: _T('scenes.reports.sales-by-clients.name'),
			hrefObj: { url: AppStates.reportsSalesByClients.url },
			id: `${ID_PREFIX}report-sales-by-clients`,
		},
		reportUnsettledPayments: {
			label: _T('scenes.reports.unsettled-payments.name'),
			hrefObj: { url: AppStates.reportsUnsettledPayments.url },
			id: `${ID_PREFIX}report-unsettled-payments`,
		},
		reportSalesByProducts: {
			label: _T('scenes.reports.sales-by-products.name'),
			hrefObj: { url: AppStates.reportsSalesByProducts.url },
			id: `${ID_PREFIX}report-sales-by-products`,
		},
		reportSalesByPeriod: {
			label: _T('scenes.reports.sales-by-period.name'),
			hrefObj: { url: AppStates.reportsSalesByPeriod.url },
			id: `${ID_PREFIX}report-sales-by-period`,
		},
		reportSalesWsto: {
			label: _T('scenes.reports.sales-wsto.name'),
			hrefObj: { url: AppStates.reportsSalesWsto.url },
			id: `${ID_PREFIX}report-sales-wsto`,
		},
		reportPurchasesByPeriod: {
			label: _T('scenes.reports.purchases-by-period.name'),
			hrefObj: { url: AppStates.reportsPurchasesByPeriod.url },
			id: `${ID_PREFIX}report-purchases-by-period`,
		},
		reportSaleRegistry: {
			label: _T('scenes.reports.sale-registry.name'),
			hrefObj: { url: AppStates.reportsSaleRegistry.url },
			id: `${ID_PREFIX}report-sale-registry`,
		},
		reportPurchaseRegistry: {
			label: _T('scenes.reports.purchase-registry.name'),
			hrefObj: { url: AppStates.reportsPurchaseRegistry.url },
			id: `${ID_PREFIX}report-purchase-registry`,
		},
		reportInventoryStocktaking: {
			label: _T('scenes.reports.inventory-stocktaking.name'),
			hrefObj: { url: AppStates.reportsInventoryStocktaking.url },
			id: `${ID_PREFIX}report-inventory-stocktaking`,
		},
		unsettledDocuments: {
			label: _T('navigation.item.unsettled-documents'),
			hrefObj: { url: AppStates.unsettledDocuments.url },
			id: `${ID_PREFIX}unsettled-documents`,
		},
		eInvoicesReceived: {
			label: _T('navigation.item.e-invoice-received'),
			hrefObj: { url: AppStates.eInvoicesReceived.url },
			id: `${ID_PREFIX}e-invoice-received`,
		},
		inventory: {
			label: _T('navigation.item.inventory'),
			icon: 'box-multiple-regular',
			hrefObj: { url: AppStates.inventoryLevel.url },
			list: [] as MenuItem[],
			isActive: () => false,
			id: `${ID_PREFIX}inventory`,
		},
		inventoryLevel: {
			label: _T('navigation.item.inventory-stock-level'),
			hrefObj: { url: AppStates.inventoryLevel.url },
			id: `${ID_PREFIX}stock-level-service`,
		},
		inventoryGoodsIssue: {
			label: _T('navigation.item.inventory-goods-issue'),
			hrefObj: { url: AppStates.inventoryGoodsIssue.url },
			addHrefObj: { url: AppStates.inventoryGoodsIssueNew.url, params: { kind: InventoryDocumentKind.GoodsIssue } },
			id: `${ID_PREFIX}goods-issue-service`,
		},
		inventoryGoodsReceipt: {
			label: _T('navigation.item.inventory-goods-receipt'),
			hrefObj: { url: AppStates.inventoryGoodsReceipt.url },
			addHrefObj: { url: AppStates.inventoryGoodsReceiptNew.url, params: { kind: InventoryDocumentKind.GoodsReceipt } },
			id: `${ID_PREFIX}goods-receipt-service`,
		},
		inventoryStocktaking: {
			label: _T('navigation.item.inventory-stocktaking'),
			hrefObj: { url: AppStates.inventoryStocktaking.url },
			addHrefObj: { url: AppStates.inventoryStocktakingNew.url },
			id: `${ID_PREFIX}inventory-stocktaking`,
		},
	} satisfies Record<string, MenuItem>;
}

export async function composeMenuItems(definition: ReturnType<typeof getMenuItems>): Promise<MenuItem[]> {
	const { vatPayer } = await (inject(RootContext) as unknown as RootContextBase).getAsync(CompanyParametersQuery);

	if (vatPayer) {
		definition.documentSale.list = [
			definition.documentSaleInvoice,
			definition.documentSaleReceipt,
			definition.documentSaleAdvanceInvoice,
			definition.documentSaleInvoiceMargin,
			definition.documentSaleProForma,
			definition.documentSaleSalePattern,
			definition.unsettledDocuments
		];
	} else {
		definition.documentSale.list = [
			definition.documentSaleInvoice,
			definition.documentSaleReceipt,
			definition.documentSaleAdvanceInvoice,
			definition.documentSaleProForma,
			definition.documentSaleSalePattern,
			definition.unsettledDocuments
		];
	}

	definition.purchaseDocument.list = [
		definition.purchaseDocumentExpanse,
		definition.purchaseDocumentInvoice,
		...(withPermission(UserPermissionKind.ReceiveDocumentsFromKsef, [definition.eInvoicesReceived]) || [])
	];

	definition.inventory.list = [
		definition.inventoryLevel,
		definition.inventoryGoodsIssue,
		definition.inventoryGoodsReceipt,
		definition.inventoryStocktaking
	];

	return [
		definition.dashboard,
		definition.documentSale,
		...(withFeature('artemisModuleFeature', [definition.orders]) || []),
		definition.clients,
		definition.products,
		...(withModule('Inventory', [definition.inventory]) || []),
		definition.purchaseDocument,
		definition.accountancy,
		definition.reports
	];
}
